import { Observable, of, zip } from 'rxjs';
import { CardSet, CardSymbol } from '@deckplan.io/common';
import { filter, map, switchMap } from 'rxjs/operators';
import { CardSymbolService } from './card-symbol.service';
import { CardSetService } from './card-set.service';
import { Injectable } from '@angular/core';
import { Mockable } from '../mocks/mockable.decorator';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';


export interface CardSetAndSafeSvg {
    cardSet: CardSet;
    safeSvg: SafeHtml;
}

@Injectable({
    providedIn: 'root'
})
@Mockable
export class CardSymbologyUtils {

    private readonly svgNamespace = 'http://www.w3.org/2000/svg';

    private readonly stops = [
        {
            color: 'var(--color-alt)',
            offset: '10%'
        },
        {
            color: 'var(--color-main)',
            offset: '50%'
        },
        {
            color: 'var(--color-alt)',
            offset: '90%'
        }
    ];

    // Map of set code to map of rarity to SVG SafeHtml, i.e. Map<setCode, Map<rarity, SafeHtml>>
    private readonly gradientCache = new Map<string, Map<string, SafeHtml>>();

    private readonly slashSvg = 'assets/slash.svg';

    constructor(private readonly cardSymbolService: CardSymbolService,
                private readonly cardSetService: CardSetService,
                private readonly sanitizer: DomSanitizer) {}

    public getManaSymbols(item: any, field: string): Observable<CardSymbol[]> {
        if (item.cardFaces && Array.isArray(item.cardFaces) && item.cardFaces.length) {
            const one = item.cardFaces[0].mana_cost;
            const two = item.cardFaces[1].mana_cost;
            const separator = item.cardFaces.filter(face => face.mana_cost.length).length > 1 ? '//' : '';
            return this.getSymbolsFromString(`${one}${separator}${two}`);
        }

        return this.getSymbolsFromString(item[field]);
    }

    public getSymbolsFromString(symbolsString: string): Observable<CardSymbol[]> {
        if (!symbolsString || !symbolsString.length) {
            return of([]);
        }

        // Symbols are surrounded by braces, so match for those
        const manaRegex = /(\{.+?\}|\/\/)/g;
        const symbols: string[] = [];

        let regexResults = manaRegex.exec(symbolsString);
        while (regexResults !== null) {
            const manaSymbol = regexResults[1];
            symbols.push(manaSymbol);
            regexResults = manaRegex.exec(symbolsString);
        }
        return this.getSymbols(symbols);
    }

    public getSymbols(symbols: string[]): Observable<CardSymbol[]> {
        // If the strings aren't surrounded by { and }, then surround them
        const cardSymbolsObs = symbols.map(symbol => {
            if (symbol === '//') {
                return of({svgUri: this.slashSvg, english: ''} as any);
            } else {
                return this.cardSymbolService.getOne(symbol).pipe(filter(s => s !== undefined));
            }
        });
        return zip(...cardSymbolsObs);
    }

    public getCardSet(setCode: string, rarity: string): Observable<CardSetAndSafeSvg> {
        return this.cardSetService.getOneByCode(setCode).pipe(
            filter((set) => set !== undefined),
            switchMap((set: CardSet) => {
                if (!this.gradientCache.has(set.id) || !this.gradientCache.get(set.id).has(rarity)) {
                    if (!this.gradientCache.has(set.id)) {
                        this.gradientCache.set(set.id, new Map<string, SafeHtml>());
                    }
                    this.gradientCache.get(set.id).set(rarity, this.sanitizer.bypassSecurityTrustHtml(this.constructGradientSvg(set, rarity)));
                }

                const modifiedSvg = this.gradientCache.get(set.id).get(rarity);

                return of({cardSet: set, safeSvg: modifiedSvg});
            })
        );
    }

    // Modifies a CardSet's svgPath to include a nice fancy gradient
    private constructGradientSvg(set: CardSet, rarity: string): string {
        const document = (new DOMParser()).parseFromString(set.svgPath, 'text/html');
        const svgElement = document.getElementsByTagName('svg').item(0);
        if (!svgElement) {
            return '';
        }
        // Make sure the svg doesn't get too big
        svgElement.classList.add('set-symbol');

        const paths = document.getElementsByTagName('path');

        // Build the structure we need to put the SVG linearGradient tag into
        const defs = document.createElementNS(this.svgNamespace, 'defs');
        const linearGradient = document.createElementNS(this.svgNamespace, 'linearGradient');

        // Parses an array of stop information and appends <stop> elements to the <linearGradient>
        for (const stopData of this.stops) {
            const stop = document.createElementNS(this.svgNamespace, 'stop');
            stop.setAttribute('offset', stopData.offset);
            stop.setAttribute('stop-color', stopData.color);

            // Add the stop to the <linearGradient> element.
            linearGradient.appendChild(stop);
        }

        // Set up the 45 degree angled gradient
        linearGradient.id = 'set-gradient-' + rarity;
        linearGradient.setAttribute('x1', '0%');
        linearGradient.setAttribute('x2', '100%');
        linearGradient.setAttribute('y1', '100%');
        linearGradient.setAttribute('y2', '0%');

        defs.appendChild(linearGradient);
        svgElement.insertAdjacentElement('afterbegin', defs);

        // Accounts for possibility of more than one path; tell it to use the gradient we built above
        for (let i = 0; i < paths.length; i++) {
            paths.item(i).setAttribute('class', 'set-icon-' + rarity);
        }

        return document.documentElement.innerHTML;
    }

}
